import {
  Image,
  useMediaQuery,
  Text,
  ExpandableText,
  Link,
} from '@lounge-fe/ui-kit'
import { HeroImageProps } from './types'
import classNames from 'classnames'
import { useTranslation } from 'react-i18next'

export const HeroImage = ({
  title,
  heroImage,
  heroImageDesktop,
  isTitlePrimary,
  heroImageOverlay,
  description,
}: HeroImageProps) => {
  const isDesktop = useMediaQuery(({ screens }) => `(min-width: ${screens.sm})`)
  const image = isDesktop
    ? heroImageDesktop || heroImage
    : heroImage || heroImageDesktop
  const { t } = useTranslation()

  const CHAR_THRESHOLD_DESKTOP = 150
  const CHAR_THRESHOLD_MOBILE = 100
  const charThreshold = isDesktop
    ? CHAR_THRESHOLD_DESKTOP
    : CHAR_THRESHOLD_MOBILE
  const needsExpandable = description && description.length > charThreshold

  if (!image) {
    return (
      <>
        <div
          className={classNames(
            'my-6 flex flex-col justify-center wrapper wrapper--size-fluid max-w-full px-6 lg:px-10 items-center',
          )}
        >
          <Text
            variant="title-four"
            as={isTitlePrimary ? 'h1' : 'h3'}
            className="text-center"
          >
            {title}
          </Text>
          {description && (
            <div className="mt-4 sm:mt-2 sm:max-w-[800px]">
              {needsExpandable ? (
                <ExpandableText
                  className={({ isExpanded }) =>
                    classNames(!isExpanded && 'flex flex-row items-start gap-1')
                  }
                >
                  {({ isExpanded }) => (
                    <>
                      <ExpandableText.Content
                        variant="body-sm"
                        className={classNames(!isExpanded && 'line-clamp-1')}
                      >
                        {description}
                      </ExpandableText.Content>
                      <ExpandableText.Trigger>
                        <Link
                          as="button"
                          type="button"
                          variant="body-sm"
                          className="underline whitespace-nowrap"
                        >
                          {isExpanded
                            ? t('action.read_less')
                            : t('action.read_more')}
                        </Link>
                      </ExpandableText.Trigger>
                    </>
                  )}
                </ExpandableText>
              ) : (
                <Text variant="body-sm">{description}</Text>
              )}
            </div>
          )}
        </div>
      </>
    )
  }

  return (
    <>
      <div className="collection-hero-image relative w-full h-full">
        <div
          className={classNames(
            'collection-hero-image__image h-full',
            heroImageOverlay && 'overlay',
          )}
        >
          <Image
            src={image.src}
            alt={image.alt}
            className="w-full h-full object-cover max-h-[248px] sm:max-h-[440px]"
            width={image.width}
            height={image.height}
            loading="eager"
            fetchPriority="high"
          />
        </div>
        <div className="collection-hero-image__text absolute inset-0 flex items-center justify-center">
          <Text
            variant={isDesktop ? 'title-three' : 'title-five'}
            as={isTitlePrimary ? 'h1' : 'h3'}
            className="text-center text-inverse"
          >
            {title}
          </Text>
        </div>
      </div>
      {description && (
        <div className="mb-4 sm:mb-6 py-3 px-4 sm:max-w-[800px] self-center">
          {needsExpandable ? (
            <ExpandableText
              className={({ isExpanded }) =>
                classNames(!isExpanded && 'flex flex-row items-start gap-1')
              }
            >
              {({ isExpanded }) => (
                <>
                  <ExpandableText.Content
                    variant="body-sm"
                    className={classNames(!isExpanded && 'line-clamp-1')}
                  >
                    {description}
                  </ExpandableText.Content>
                  <ExpandableText.Trigger>
                    <Link
                      as="button"
                      type="button"
                      variant="body-sm"
                      className="underline whitespace-nowrap"
                    >
                      {isExpanded
                        ? t('action.read_less')
                        : t('action.read_more')}
                    </Link>
                  </ExpandableText.Trigger>
                </>
              )}
            </ExpandableText>
          ) : (
            <Text variant="body-sm">{description}</Text>
          )}
        </div>
      )}
    </>
  )
}
